// Explosions - the satisfying part
// Just expanding circles that fade out

import { Entity } from '../core/Entity';
import { VectorRenderer } from '../graphics/VectorRenderer';

interface Particle {
  cos: number; // Pre-computed cos(angle)
  sin: number; // Pre-computed sin(angle)
  speed: number;
  distance: number;
}

export class Explosion extends Entity {
  private lifetime: number;
  private maxLifetime: number;
  private maxRadius: number;
  private currentRadius: number;
  private particles: Particle[];
  // Reusable Vec2 objects to avoid allocations per frame
  private startPoint = { x: 0, y: 0 };
  private endPoint = { x: 0, y: 0 };

  constructor(x: number, y: number, size: 'small' | 'medium' | 'large' = 'medium') {
    super(x, y);

    this.lifetime = 0;
    let particleCount: number;

    switch (size) {
      case 'small':
        this.maxLifetime = 0.3;
        this.maxRadius = 20;
        particleCount = 6;
        break;
      case 'medium':
        this.maxLifetime = 0.5;
        this.maxRadius = 40;
        particleCount = 10;
        break;
      case 'large':
        this.maxLifetime = 0.8;
        this.maxRadius = 60;
        particleCount = 16;
        break;
    }

    this.currentRadius = 0;

    // Generate particles with pre-computed trig values
    this.particles = [];
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.3;
      this.particles.push({
        cos: Math.cos(angle),
        sin: Math.sin(angle),
        speed: 50 + Math.random() * 100,
        distance: 0,
      });
    }
  }

  update(dt: number): void {
    if (!this.active) return;

    this.lifetime += dt;
    if (this.lifetime >= this.maxLifetime) {
      this.destroy();
      return;
    }

    // Expand radius
    const progress = this.lifetime / this.maxLifetime;
    this.currentRadius = this.maxRadius * Math.sqrt(progress); // sqrt for fast start

    // Update particles
    for (let i = 0; i < this.particles.length; i++) {
      this.particles[i].distance += this.particles[i].speed * dt;
    }
  }

  render(renderer: VectorRenderer): void {
    if (!this.active) return;

    const progress = this.lifetime / this.maxLifetime;
    const alpha = 1 - progress;

    // Color fades from white to orange to red
    let color: string;
    if (progress < 0.2) {
      color = '#fff';
    } else if (progress < 0.5) {
      color = '#ff0';
    } else {
      color = '#f80';
    }

    renderer.setGlow(10 * alpha, color);

    // Draw expanding circle
    renderer.drawCircle(this.position, this.currentRadius, 12, color);

    // Draw particles using pre-computed trig and reusable points
    const posX = this.position.x;
    const posY = this.position.y;
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      const px = posX + p.cos * p.distance;
      const py = posY + p.sin * p.distance;

      this.startPoint.x = px - 2;
      this.startPoint.y = py - 2;
      this.endPoint.x = px + 2;
      this.endPoint.y = py + 2;

      renderer.drawLine(this.startPoint, this.endPoint, color, 1);
    }

    renderer.clearGlow();
  }
}
